// components/dashboard/EnquiryDetailModal.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Paper,
  Grid,
  Link,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplyIcon from "@mui/icons-material/Reply";

const formatDateTime = (dateStr) => {
  return new Date(dateStr).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const InfoCard = ({ icon, label, value, isLink, linkHref, iconColor }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1,
      borderRadius: 2.5,
      borderColor: "grey.200",
      height: "100%",
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: `${iconColor}15`,
          color: iconColor,
          borderRadius: 1.5,
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: "0.6rem",
            display: "block",
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        {isLink && value ? (
          <Link
            href={linkHref}
            underline="hover"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              wordBreak: "break-word",
              display: "block",
            }}
          >
            {value}
          </Link>
        ) : (
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: value ? "text.primary" : "text.disabled",
              wordBreak: "break-word",
              lineHeight: 1.4,
            }}
          >
            {value || "—"}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const EnquiryDetailModal = ({ enquiry, onClose, onDelete }) => {
  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        elevation: 10,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: "linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)",
          color: "white",
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: "rgba(255,255,255,0.18)",
                color: "white",
                fontWeight: 700,
                fontSize: "1.1rem",
                flexShrink: 0,
              }}
            >
              {enquiry.name?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>

            <Box minWidth={0}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {enquiry.name}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  label={enquiry.is_read ? "● Read" : "● New"}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: enquiry.is_read
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(255, 255, 255, 0.15)",
                    color: enquiry.is_read ? "#86efac" : "#fde68a",
                    border: enquiry.is_read
                      ? "1.5px solid rgba(134, 239, 172, 0.5)"
                      : "1.5px solid rgba(253, 230, 138, 0.5)",
                    backdropFilter: "blur(4px)",
                    "& .MuiChip-label": {
                      px: 1,
                      letterSpacing: "0.03em",
                    },
                  }}
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <AccessTimeIcon sx={{ fontSize: 13, opacity: 0.85 }} />
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.9, fontSize: "0.7rem" }}
                  >
                    {formatDateTime(enquiry.created_at)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.12)",
              flexShrink: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body - No overflow here */}
      <DialogContent
        sx={{
          p: 2.5,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 2,
        }}
      >
        {/* 2x2 Contact Grid */}
        <Grid container spacing={1.5} sx={{ flexShrink: 0 }}>
          <Grid item xs={6}>
            <InfoCard
              icon={<PersonIcon />}
              iconColor="#1976d2"
              label="Full Name"
              value={enquiry.name}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<EmailIcon />}
              iconColor="#7b1fa2"
              label="Email"
              value={enquiry.email}
              isLink
              linkHref={`mailto:${enquiry.email}`}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<PhoneIcon />}
              iconColor="#2e7d32"
              label="Phone"
              value={enquiry.phone}
              isLink={!!enquiry.phone}
              linkHref={`tel:${enquiry.phone}`}
            />
          </Grid>
          <Grid item xs={6}>
            <InfoCard
              icon={<BusinessIcon />}
              iconColor="#ed6c02"
              label="Company"
              value={enquiry.company}
            />
          </Grid>
        </Grid>

        {/* Message Section - Only this scrolls */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            borderColor: "grey.200",
            overflow: "hidden",
            bgcolor: "grey.50",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 150,
            maxHeight: 220,
          }}
        >
          {/* Message Header */}
          <Box
            sx={{
              px: 2,
              py: 1.2,
              borderBottom: "1px solid",
              borderColor: "grey.200",
              bgcolor: "background.paper",
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "primary.50",
                  color: "primary.main",
                  borderRadius: 1.5,
                }}
              >
                <MessageIcon sx={{ fontSize: 15 }} />
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: "0.8rem",
                }}
              >
                Message / Requirement
              </Typography>
            </Stack>
          </Box>

          {/* Scrollable Message Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1.5,
              "&::-webkit-scrollbar": {
                width: 5,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c7d0",
                borderRadius: 10,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.82rem",
              }}
            >
              {enquiry.message || "—"}
            </Typography>
          </Box>
        </Paper>
      </DialogContent>

      {/* Footer - Fixed */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
          flexShrink: 0,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={() => onDelete(enquiry.id)}
          sx={{
            borderRadius: 1,
            px: 2,
            height: 36,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.78rem",
            minWidth: 100,
          }}
        >
          Delete
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
          href={`mailto:${enquiry.email}`}
          component="a"
          sx={{
            borderRadius: 1,
            px: 2,
            height: 36,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.78rem",
            minWidth: 100,
          }}
        >
          Reply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnquiryDetailModal;
